const Base64EncodeInfo = () => {
  return (
    <>
      <span className="mb-4 d-block">
        Base64 encoding is a method used to convert binary data into an ASCII
        text format, facilitating the safe and efficient transmission of data
        across systems that are designed to handle text. This encoding scheme is
        particularly useful in contexts where data integrity and compatibility
        are crucial, such as in email systems, web development, and data
        serialization formats like XML and JSON.
      </span>
      <strong className="d-block mb-4">Mechanism of Base64 Encoding</strong>
      <ol>
        <li>
          Purpose and Rationale:
          <span className="mt-3 mb-3 d-block">
            Base64 encoding addresses the challenge of transmitting binary data
            over media that are designed to handle text. Binary data can include
            byte sequences that correspond to control characters, which can be
            misinterpreted or corrupted during transmission. By converting
            binary data into a text format using a limited set of ASCII
            characters, Base64 ensures that the data remains intact and
            interpretable across different systems.
          </span>
        </li>
        <li>
          Conversion Process:
          <span className="mt-3 d-block mb-2">
            The process of Base64 encoding involves several steps:
          </span>
          <span className="d-block mt-2">
            <strong className="me-2 d-inline-block">
              Binary to ASCII Conversion:
            </strong>
            The primary goal of Base64 is to convert binary data into a text
            format. This is achieved by transforming the binary data into a
            series of 6-bit groups, each of which can be represented by a
            character from the Base64 alphabet.
          </span>
          <span className="d-block mt-2">
            <strong className="me-2 d-inline-block">
              Splitting into 6-bit Chunks:
            </strong>
            The input data is first converted into its binary form. This binary
            data is then divided into 24-bit segments, which are further split
            into four 6-bit units. Each 6-bit unit corresponds to a single
            Base64 character. This means that every three bytes (24 bits) of
            binary data are encoded into four Base64 characters.
          </span>
          <span className="d-block mt-2">
            <strong className="me-2 d-inline-block">
              Mapping to Base64 Characters:
            </strong>
            The 6-bit binary numbers are mapped to a set of 64 ASCII characters.
            The Base64 alphabet consists of:
          </span>
          <span className="d-block mt-2">
            - Uppercase letters A-Z (26 characters)
          </span>
          <span className="d-block">
            - Lowercase letters a-z (26 characters)
          </span>
          <span className="d-block">- Digits 0-9 (10 characters)</span>
          <span className="d-block">
            - The symbols '+' and '/' (2 characters)
          </span>
          <span className="d-block mt-2">
            Each character in the Base64 alphabet represents a specific 6-bit
            value, allowing for the conversion of binary data into a text
            format.
          </span>
          <span className="d-block mt-2">
            <strong className="me-2 d-inline-block">
              Padding for Remainders:
            </strong>
            If the input data does not perfectly divide into 24-bit segments,
            padding is used to complete the final segment. This is necessary
            because Base64 encoding requires that the output length be a
            multiple of four characters. Padding is achieved by appending '='
            characters to the output:
          </span>
          <span className="d-block">
            - If one byte of data remains, it is padded with two '=' symbols.
          </span>
          <span className="d-block mb-3">
            - If two bytes remain, it is padded with one '=' symbol.
          </span>
        </li>
        <li>
          Output Characteristics:
          <span className="mt-3 d-block mb-4">
            The Base64 encoding process results in an output string whose length
            is always a multiple of four characters. This ensures alignment and
            facilitates the decoding process. However, it also means that the
            encoded data is approximately 33% larger than the original binary
            data. This increase in size is a trade-off for the benefits of data
            integrity and compatibility.
          </span>
        </li>
      </ol>
      <strong className="d-block mb-4">Applications and Limitations</strong>
      <span className="d-block mb-2">
        Base64 encoding is used in a variety of applications where binary data
        needs to be represented as text:
      </span>
      <span className="d-block mt-2">
        <strong className="me-2 d-inline-block">Email Systems:</strong>
        In email, Base64 is used to encode binary attachments, such as images
        and documents, into a text format that can be transmitted via SMTP. This
        ensures that the attachments are not corrupted during transmission.
      </span>
      <span className="d-block mt-2">
        <strong className="me-2 d-inline-block">Web Development:</strong>
        Base64 is used to embed images directly into HTML or CSS files. This
        reduces the number of HTTP requests needed to load a webpage,
        potentially improving load times. However, it also increases the size of
        the HTML or CSS file.
      </span>
      <span className="d-block mt-2 mb-4">
        <strong className="me-2 d-inline-block">Data Serialization:</strong>
        Formats like XML and JSON use Base64 to encode binary data within text
        records. This allows for the structured interchange of data between
        different systems, ensuring compatibility and integrity.
      </span>
      <span className="d-block">
        Despite its advantages, Base64 has limitations, such as increased data
        size and computational overhead. It is not a form of encryption and does
        not provide data security. For secure data transmission, additional
        encryption methods should be used alongside Base64. Nonetheless, Base64
        remains an essential technique for handling binary data in a wide range
        of applications.
      </span>
    </>
  );
};

export default Base64EncodeInfo;
